import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { isEqual, orderBy } from 'lodash'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Loading } from 'components/UI'
import { List } from 'ui'
import { Button } from 'components/Forms'
import { closeModal } from 'store/actions/modals'
import { t } from 'translations'
import { Trans } from 'react-i18next'
import { getPositionName, getUserName } from 'services/Utilities'
import { Format, Parse } from 'services/Dates'
import { Breaks, Transfers, WagePremiums } from 'components/Punch'

const PunchCardDiffModal = ({ punchCardId, timeZone, closeModal }) => {
  const [punchCard, setPunchCard] = useState()
  const [punchCardDeleted, setPunchCardDeleted] = useState(false)

  // Function to load the information of the timeOff (if we are modifying it)
  useEffect(() => {
    loadPunchCard(punchCardId)

    async function loadPunchCard(_id) {
      const { result, success } = await Api.get(`/punch/cards/${_id}`)
      if (!success || !result) return setPunchCardDeleted(true)
      setPunchCard(result)
    }
  }, [punchCardId])

  const parseValue = ({ property, before, after }) => {
    // Helper functions
    const noValue = value => !value || value === ''
    const formatDate = date => {
      if (noValue(date)) return date
      try {
        return Format(Parse(date), { outFormat: 'humanDateTime' })
      } catch (error) {
        return date
      }
    }

    let transKey = ''

    // If the before and after values are null, do not return
    if (isEqual(before, after)) return null

    switch (property) {
      case 'userId':
        if (noValue(before)) transKey = 'EMPLOYEEID_ASSIGNED'
        else if (noValue(after)) transKey = 'EMPLOYEEID_UNASSIGNED'
        else transKey = 'EMPLOYEEID_CHANGED'
        before = getUserName(before)
        after = getUserName(after)
        break
      case 'shiftId':
        if (noValue(before)) transKey = 'SHIFT_ASSOCIATED'
        else if (noValue(after)) transKey = 'SHIFT_UNASSOCIATED'
        else transKey = 'SHIFT_CHANGED'
        break
      case 'positionId':
        transKey = 'POSITIONID_CHANGED'
        before = getPositionName(before)
        after = getPositionName(after)
        break
      case 'arrival':
        if (noValue(before)) transKey = 'ARRIVAL_ADDED'
        else if (noValue(after)) transKey = 'ARRIVAL_REMOVED'
        else transKey = 'ARRIVAL_CHANGED'
        before = formatDate(before)
        after = formatDate(after)
        break
      case 'leave':
        if (noValue(before)) transKey = 'LEAVE_ADDED'
        else if (noValue(after)) transKey = 'LEAVE_REMOVED'
        else transKey = 'LEAVE_CHANGED'
        before = formatDate(before)
        after = formatDate(after)
        break
      case 'breaks': {
        const simplifyBreaks = breaks => breaks.map(({ start, end, paid, reason }) => ({ start, end, paid, reason }))
        // If the before and after values are null, do not return
        if (isEqual(simplifyBreaks(before), simplifyBreaks(after))) return null
        return (
          <>
            <i>{t('manager.punch.diffs.BREAKS_CHANGED')}</i>
            <div className="row mb-1">
              <div className="col-md">
                <b>{t('manager.punch.diffs.BEFORE')}</b>
                {before && before.length ? <Breaks breaks={before} /> : <div className="font-italic">{t('manager.punch.diffs.NO_BREAKS')}</div>}
              </div>
              <div className="col-md">
                <b>{t('manager.punch.diffs.AFTER')}</b>
                {after && after.length ? <Breaks breaks={after} /> : <div className="font-italic">{t('manager.punch.diffs.NO_BREAKS')}</div>}
              </div>
            </div>
          </>
        )
      }
      case 'transfers': {
        const simplifyTransfers = transfers => transfers.map(({ time, toPosition, toProject }) => ({ time, toPosition, toProject }))
        // If the before and after values are null, do not return
        if (isEqual(simplifyTransfers(before), simplifyTransfers(after))) return null
        return (
          <>
            <i>{t('manager.punch.diffs.TRANSFERS_CHANGED')}</i>
            <div className="row mb-1">
              <div className="col-md">
                <b>{t('manager.punch.diffs.BEFORE')}</b>
                {before && before.length ? <Transfers transfers={before} /> : <div className="font-italic">{t('manager.punch.diffs.NO_TRANSFERS')}</div>}
              </div>
              <div className="col-md">
                <b>{t('manager.punch.diffs.AFTER')}</b>
                {after && after.length ? <Transfers transfers={after} /> : <div className="font-italic">{t('manager.punch.diffs.NO_TRANSFERS')}</div>}
              </div>
            </div>
          </>
        )
      }
      case 'wagePremiums': {
        // If the before and after values are null, do not return
        if (isEqual(before, after)) return null
        return (
          <>
            <i>{t('manager.punch.diffs.WAGE_PREMIUMS_CHANGED')}</i>
            <div className="row mb-1">
              <div className="col-md">
                <b>{t('manager.punch.diffs.BEFORE')}</b>
                {before && before.length ? (
                  <WagePremiums wagePremiums={before} />
                ) : (
                  <div className="font-italic">{t('manager.punch.diffs.NO_WAGE_PREMIUMS')}</div>
                )}
              </div>
              <div className="col-md">
                <b>{t('manager.punch.diffs.AFTER')}</b>
                {after && after.length ? <WagePremiums wagePremiums={after} /> : <div className="font-italic">{t('manager.punch.diffs.NO_WAGE_PREMIUMS')}</div>}
              </div>
            </div>
          </>
        )
      }
      case 'status':
        transKey = 'STATUS_CHANGED'
        before = t(`manager.punch.STATUSES.${before}`)
        after = t(`manager.punch.STATUSES.${after}`)
        break
      case 'disableArrivalRounding':
        if (after) transKey = 'ARRIVAL_ROUNDING_DISABLED'
        else transKey = 'ARRIVAL_ROUNDING_ENABLED'
        break
      case 'disableLeaveRounding':
        if (after) transKey = 'LEAVE_ROUNDING_DISABLED'
        else transKey = 'LEAVE_ROUNDING_ENABLED'
        break

      case 'approvedManually':
        if (after) transKey = 'APPROVED_MANUALLY'
        else transKey = 'UNAPPROVED_MANUALLY'
        break
      default:
        break
    }

    if (transKey === '') return null

    return (
      <>
        <Trans t={t} i18nKey={`manager.punch.diffs.${transKey}`}>
          <b>{{ before }}</b>
          <b>{{ after }}</b>
        </Trans>
      </>
    )
  }

  const getModalContent = () => {
    if (!punchCard && !punchCardDeleted) return <Loading />

    if (punchCardDeleted)
      return (
        <>
          {t('manager.punch.diffs.PUNCH_CARD_DELETED')}
          <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} />
        </>
      )

    const sortedDiffs = orderBy(punchCard.diffs, ['lastUpdated'])

    return (
      <>
        <List className="mt-2">
          {sortedDiffs.map(({ lastUpdated, by, values = [] }, index) => (
            <List.Item key={index}>
              {/* Show who and when */}
              <Trans t={t} i18nKey="manager.punch.diffs.TIMESTAMP">
                <b>{{ name: getUserName(by) }}</b>
                <b>{{ time: Format(lastUpdated, { outFormat: 'time', isUTC: true, timeZone }) }}</b>
                {{ date: Format(lastUpdated, { outFormat: 'humanDate', isUTC: true, timeZone }) }}
              </Trans>

              {/* Show what changed */}
              {values.map((value, index) => (
                <div key={index}>{parseValue(value)}</div>
              ))}
            </List.Item>
          ))}
          {!sortedDiffs.length ? <List.Item>{t('components.NOTHING_TO_DISPLAY')}</List.Item> : null}
        </List>
        <div className="mt-3">
          <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} />
        </div>
      </>
    )
  }

  return (
    <BaseModal hideButtons title="manager.punch.diffs">
      {getModalContent()}
    </BaseModal>
  )
}

const mapStateToProps = ({ settings }) => ({ timeZone: settings.defaultTimeZone })
const mapDispatchToProps = dispatch => ({ closeModal: () => dispatch(closeModal()) })

export default connect(mapStateToProps, mapDispatchToProps)(PunchCardDiffModal)
